import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { H2, H3 } from '@liveauctioneers/hammer-ui-core/text';
import { ReactElement, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { FilterTerm } from '@/pages/Search/Components/FilterTerm';
import { useAppSelector } from '@/redux/hooks';
import { getLocale } from '@/redux/modules/intl';

export type AuctionsNearMeFilterBarProps = {
    numberOfAuctions: number;
    onKeywordsChange: (keywords: string[]) => void;
    toggleLocalPickUpFilter: () => void;
    toggleTopRatedFilter: () => void;
};

const AuctionsNearMeFilterBar = ({
    numberOfAuctions,
    onKeywordsChange,
    toggleLocalPickUpFilter,
    toggleTopRatedFilter,
}: AuctionsNearMeFilterBarProps): ReactElement => {
    const intl = useIntl();
    const locale = useAppSelector(getLocale);
    const filterByKeywordPlaceholder = intl.formatMessage({
        id: 'filter_by_keyword',
    });
    const [keywords, setKeywords] = useState<string[]>([]);
    const isMobile = useIsMobileLayout();

    const addKeyword = (keyword: string): void => {
        const keywordAlreadyExists = keywords.find(
            (existingKeyword) => existingKeyword.toLowerCase() === keyword.toLowerCase()
        );
        if (!keywordAlreadyExists) {
            setKeywords([...keywords, keyword]);
        }
    };

    const removeKeyword = (keyword: string): void => {
        const keywordsClone = keywords.slice();
        keywordsClone.splice(keywordsClone.indexOf(keyword), 1);
        setKeywords(keywordsClone);
    };

    useEffect(() => {
        onKeywordsChange(keywords);
    }, [keywords, onKeywordsChange]);

    const hasKeywordFilters = keywords.length > 0;

    const desktopLayout = !isMobile ? (
        <StyledDesktopLayout>
            <StyledDesktopInputs>
                <FilterTerm
                    placeholderMessage={filterByKeywordPlaceholder}
                    shortOnMobile
                    submitFilterTerm={addKeyword}
                />
                <StyledFilterChipsContainer>
                    <Chip onClick={toggleTopRatedFilter}>
                        <FormattedMessage id="topRatedCount" />
                    </Chip>
                    <Chip onClick={toggleLocalPickUpFilter}>
                        <FormattedMessage id="freeLocalPickupCount" />
                    </Chip>
                </StyledFilterChipsContainer>
            </StyledDesktopInputs>
            {hasKeywordFilters && (
                <>
                    {keywords.map((keyword) => (
                        <StyledKeywordChip key={`auctions-near-me-keyword-filter-${keyword}`}>
                            <Chip
                                aria-label={`Remove ${keyword} filter`}
                                className="max-w-[77px]"
                                key={`auctions-near-me-keyword-filter-${keyword}`}
                                onClose={() => removeKeyword(keyword)}
                                selected
                                style="input"
                            >
                                {keyword}
                            </Chip>
                        </StyledKeywordChip>
                    ))}
                </>
            )}
        </StyledDesktopLayout>
    ) : null;

    const mobileLayout = isMobile ? (
        <span>
            <StyledMobileInputs>
                <FilterTerm
                    placeholderMessage={filterByKeywordPlaceholder}
                    shortOnMobile
                    submitFilterTerm={addKeyword}
                />
                <StyledFilterChipsContainer>
                    <Chip onClick={toggleTopRatedFilter}>
                        <FormattedMessage id="topRatedCount" />
                    </Chip>
                    <Chip onClick={toggleLocalPickUpFilter}>
                        <FormattedMessage id="freeLocalPickupCount" />
                    </Chip>
                </StyledFilterChipsContainer>
            </StyledMobileInputs>
            {hasKeywordFilters && (
                <StyledMobileKeywordChips>
                    {keywords.map((keyword) => (
                        <StyledKeywordChip>
                            <Chip
                                aria-label={`Remove ${keyword} filter`}
                                className="max-w-[77px]"
                                key={`auctions-near-me-keyword-filter-${keyword}`}
                                onClose={() => removeKeyword(keyword)}
                                selected
                                style="input"
                            >
                                {keyword}
                            </Chip>
                        </StyledKeywordChip>
                    ))}
                </StyledMobileKeywordChips>
            )}
        </span>
    ) : null;

    return (
        <StyledFilterBarLayout data-testid="auctionsSection">
            <div>
                <ResultsCountContainer>
                    {isMobile ? (
                        <H2 data-testid="auctionCount">
                            {numberOfAuctions === 1 ? (
                                <FormattedMessage id="nearby_auctions_count_single" />
                            ) : (
                                <FormattedMessage
                                    id="nearby_auctions_count"
                                    values={{
                                        numberOfAuctions: Intl.NumberFormat(locale).format(numberOfAuctions),
                                    }}
                                />
                            )}
                        </H2>
                    ) : (
                        <H3 data-testid="auctionCount">
                            {numberOfAuctions === 1 ? (
                                <FormattedMessage id="nearby_auctions_count_single" />
                            ) : (
                                <FormattedMessage
                                    id="nearby_auctions_count"
                                    values={{
                                        numberOfAuctions: Intl.NumberFormat(locale).format(numberOfAuctions),
                                    }}
                                />
                            )}
                        </H3>
                    )}
                </ResultsCountContainer>
            </div>
            {desktopLayout || mobileLayout}
        </StyledFilterBarLayout>
    );
};

export default AuctionsNearMeFilterBar;

const StyledFilterBarLayout = styled.section`
    display: flex;
    flex-direction: column;
    align-content: center;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 20px;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin-top: 0;
    }
`;

const StyledDesktopLayout = styled.span`
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        display: flex;
        flex-wrap: wrap;
        margin-top: 12px;
    }
    [data-testid='text'] {
        ${({ theme }) => css`
            font-family: ${theme.typography.fontPrimary};
            color: ${theme.colors.grey300};
        `}
        font-size: 12px;
        letter-spacing: 1px;
        line-height: 18px;
        vertical-align: middle;
    }
    [data-testid='select'] {
        width: 90px;
    }
`;

const StyledDesktopInputs = styled.span`
    display: flex;
    margin-top: 8px;
`;

const StyledMobileInputs = styled.div`
    margin-top: 8px;
`;

const StyledFilterChipsContainer = styled.span`
    display: flex;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 8px;
    }
`;

const ResultsCountContainer = styled.div`
    flex-grow: 1;
    align-self: center;
    margin-right: 8px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 32px;
    }
`;

const StyledKeywordChip = styled.div`
    margin-top: 8px;
`;

const StyledMobileKeywordChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 8px;
`;
